import React, { useEffect, useState } from "react";
import styles from "./SelectInput.module.css";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css";
import Select, { components } from "react-select";
import {
  FieldProps,
  getFieldContainerStyle,
} from "../InputFieldsController/InputFieldsController.utils";
import LabeledInputWrapper from "../InputFieldsController/LabeledInputWrapper";

const CheckboxOption = (props: any) => (
  <components.Option {...props}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        style={{ accentColor: "var(--theme1_primary)", cursor: "pointer" }}
      />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

interface SelectInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
  onInputChange?: (id: string, value: string, actionMeta?: any) => void;
  menuPlacement?: "top" | "bottom" | "auto";
  disablePortal?: boolean;
}

/**
 * SelectInput renders a dropdown using `react-select`.
 * It supports static and dynamic API-based options.
 */
const SelectInput: React.FC<SelectInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect,
  onInputChange,
  menuPlacement = "top",
  disablePortal = false,
}) => {
  const {
    id,
    value,
    options = [],
    placeholder,
    example,
    disabled,
    isValid = true,
    error,
    show = true,
    selected,
    apiConfig,
    isMulti = false,
  } = field;

  const containerStyle = getFieldContainerStyle(field);

  const dropdownOptions = apiConfig?.fetchedOptions?.length
    ? apiConfig.fetchedOptions
    : options;

  const [selectedOption, setSelectedOption] = useState<any>(isMulti ? [] : null);

  // Sync selected option after options or value update
  useEffect(() => {
    if (isMulti) {
      const values = Array.isArray(value) ? value : [];
      const matches = dropdownOptions.filter((opt: any) =>
        values.some((v: any) => String(opt.value) === String(v))
      );
      setSelectedOption(
        matches.length > 0 || values.length === 0
          ? matches
          : values.map((v: any) => ({ value: v, label: String(v) }))
      );
      return;
    }

    // This effect keeps the dropdown label in sync with the stored value and prevents it from disappearing during async option updates.
    const match =
      dropdownOptions.find((opt: any) => String(opt.value) === String(value)) || null;
    if (match) { setSelectedOption(match); return; }
    if (selectedOption && String(selectedOption.value) === String(value)) { return; }
    setSelectedOption(null);
  }, [dropdownOptions, value]);

  if (!show) return null;

  return (
    <div style={containerStyle}>
      <LabeledInputWrapper
        field={{
          ...field,
          showSourceToggle: !!field.apiConfig?.apiKey,
          onSourceReset: () =>
            handleChange(id, {
              refreshApiSource: true,
            }),
        }}
      >
        <Select
          inputId={id}
          isMulti={isMulti}
          value={selectedOption}
          onChange={(selected) => handleChange(id, selected)}
          onInputChange={(inputValue, actionMeta) => {
            if (onInputChange && actionMeta.action === "input-change") {
              onInputChange(field.id, inputValue, actionMeta);
            }
          }}
          onBlur={() => handleBlur(id)}
          onFocus={() => handleSelect(id)}
          options={dropdownOptions}
          isDisabled={disabled}
          placeholder={placeholder}
          classNamePrefix={!isValid ? "is-invalid" : "react-select"}
          className={`${styles.customSelectInput} ${
            selected ? CommonStyles.selectedField : ""
          }`}
          menuPortalTarget={disablePortal ? undefined : document.body}
          menuPlacement={menuPlacement}
          isLoading={!!field.apiConfig?.isFetching}
          loadingMessage={() => (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px" }}>
              <div className="spinner-border spinner-border-sm" role="status" style={{ color: "var(--theme1_tertiary)" }}/>
              <span>Loading options...</span>
            </div>
          )}
          {...(isMulti && {
            components: { Option: CheckboxOption },
            hideSelectedOptions: false,
            closeMenuOnSelect: false,
          })}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />

        {!isValid && error && (
          <div className="invalid-feedback d-block">{error}</div>
        )}
      </LabeledInputWrapper>

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default SelectInput;
